import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecondaryButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Camera, 
  User, 
  Bell, 
  Shield, 
  Palette, 
  Moon, 
  Sun, 
  LogOut,
  Upload,
  Trash2,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import DeleteAccountButton from '@/components/Profile/DeleteAccountButton';
import profileHeroBg from '@/assets/profile-hero-bg.jpg';

const Settings = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading, updateProfile, refetchProfile } = useProfile();
  const { isSharing, toggleLocationSharing, loading: locationLoading } = useLocationTracking();
  const [uploading, setUploading] = useState(false);
  const [notifications, setNotifications] = useState({
    likes: true,
    comments: true,
    follows: true,
    posts: true,
  });
  const [privacy, setPrivacy] = useState({
    profilePublic: true,
    showFollowers: true,
    showFollowing: true,
  });

  // Initialize privacy settings from profile
  useEffect(() => {
    if (profile) {
      setPrivacy(prev => ({
        ...prev,
        profilePublic: !profile.is_private
      }));
    }
  }, [profile]);

  // Update privacy settings in database when changed
  const handlePrivacyChange = async (field: string, value: boolean) => {
    if (!user || !profile) return;

    setPrivacy(prev => ({ ...prev, [field]: value }));

    if (field === 'profilePublic') {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ is_private: !value })
          .eq('user_id', user.id);

        if (error) throw error;
        
        toast.success(value ? 'Profilo impostato come pubblico' : 'Profilo impostato come privato');
        refetchProfile();
      } catch (error) {
        console.error('Error updating privacy settings:', error);
        toast.error('Errore nell\'aggiornamento delle impostazioni');
        // Revert the change
        setPrivacy(prev => ({ ...prev, [field]: !value }));
      }
    }
  };

  // Handle location sharing toggle
  const handleLocationToggle = async () => {
    try {
      await toggleLocationSharing();
      // Update profile share_location field to sync
      await supabase
        .from('profiles')
        .update({ share_location: !isSharing })
        .eq('user_id', user?.id);
      
      refetchProfile();
    } catch (error) {
      console.error('Error toggling location sharing:', error);
      toast.error('Errore nella gestione della condivisione posizione');
    }
  };

  // Redirect to auth if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  if (authLoading || loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        <div className="h-8 w-32 bg-muted rounded animate-pulse" />
        <Card className="glass border-white/20">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="h-6 bg-muted rounded w-48 animate-pulse" />
              <div className="h-20 bg-muted rounded animate-pulse" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="text-center py-16">
          <User className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Profilo non trovato</h3>
          <p className="text-muted-foreground">
            Non è stato possibile caricare le impostazioni del profilo.
          </p>
        </div>
      </div>
    );
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setUploading(true);
    try {
      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('profile-avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const result = await updateProfile({ avatar_url: data.publicUrl });
      
      if (result?.success) {
        toast.success('Foto profilo aggiornata!');
        refetchProfile();
      } else {
        throw new Error('Errore nell\'aggiornamento del profilo');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Errore nel caricamento della foto');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;

    try {
      const result = await updateProfile({ avatar_url: null });
      
      if (result?.success) {
        toast.success('Foto profilo rimossa!');
        refetchProfile();
      } else {
        throw new Error('Errore nella rimozione');
      }
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Errore nella rimozione della foto');
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Logout effettuato');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Errore nel logout');
    }
  };

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${profileHeroBg})` }}
    >
      <div className="max-w-2xl mx-auto p-4 pb-20 lg:pb-4">
        {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/app/profile">
          <Button variant="ghost" size="sm" className="text-white hover:text-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-white">Impostazioni</h1>
      </div>

      <div className="space-y-6">
        {/* Profile Settings */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5" />
              Profilo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20 ring-2 ring-primary/30">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                  {profile.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 space-y-2">
                <h3 className="font-semibold text-white">Foto Profilo</h3>
                <div className="flex gap-2">
                  <Label htmlFor="avatar-upload" className="cursor-pointer">
                    <SecondaryButton disabled={uploading} asChild>
                      <span>
                        {uploading ? (
                          <>
                            <div className="w-4 h-4 mr-2 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Carica Foto
                          </>
                        )}
                      </span>
                    </SecondaryButton>
                  </Label>
                  
                  {profile.avatar_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveAvatar}
                      className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Rimuovi
                    </Button>
                  )}
                </div>
                
                <Input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                
                <p className="text-xs text-muted-foreground">
                  Formato supportato: JPG, PNG. Dimensione massima: 5MB
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Profilo Pubblico</Label>
                <p className="text-sm text-muted-foreground">
                  Tutti possono vedere i tuoi post e pet. Se disattivato, solo i follower approvati potranno vedere i tuoi contenuti.
                </p>
              </div>
              <Switch
                checked={privacy.profilePublic}
                onCheckedChange={(checked) => handlePrivacyChange('profilePublic', checked)}
              />
            </div>
            
            <Separator className="bg-white/10" />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Mostra Lista Follower</Label>
                <p className="text-sm text-muted-foreground">
                  Permetti agli altri di vedere chi ti segue
                </p>
              </div>
              <Switch
                checked={privacy.showFollowers}
                onCheckedChange={(checked) => handlePrivacyChange('showFollowers', checked)}
              />
            </div>
            
            <Separator className="bg-white/10" />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Mostra Lista Seguiti</Label>
                <p className="text-sm text-muted-foreground">
                  Permetti agli altri di vedere chi stai seguendo
                </p>
              </div>
              <Switch
                checked={privacy.showFollowing}
                onCheckedChange={(checked) => handlePrivacyChange('showFollowing', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Location Settings */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Posizione
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Condividi Posizione Reale</Label>
                <p className="text-sm text-muted-foreground">
                  Permetti agli altri utenti di vedere la tua posizione sulla mappa. La posizione viene aggiornata automaticamente.
                </p>
              </div>
              <Switch
                checked={isSharing}
                onCheckedChange={handleLocationToggle}
                disabled={locationLoading}
              />
            </div>
            
            {isSharing && (
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-medium">
                      Condivisione attiva
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      La tua posizione viene condivisa con gli altri utenti sulla mappa. Puoi disattivarla in qualsiasi momento.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifiche
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Mi Piace</Label>
                <p className="text-sm text-muted-foreground">
                  Ricevi notifiche quando qualcuno mette mi piace ai tuoi post
                </p>
              </div>
              <Switch
                checked={notifications.likes}
                onCheckedChange={(checked) => 
                  setNotifications({ ...notifications, likes: checked })
                }
              />
            </div>
            
            <Separator className="bg-white/10" />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Commenti</Label>
                <p className="text-sm text-muted-foreground">
                  Ricevi notifiche per i nuovi commenti
                </p>
              </div>
              <Switch
                checked={notifications.comments}
                onCheckedChange={(checked) => 
                  setNotifications({ ...notifications, comments: checked })
                }
              />
            </div>
            
            <Separator className="bg-white/10" />
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white">Richieste di Follow</Label>
                <p className="text-sm text-muted-foreground">
                  Ricevi notifiche per nuove richieste di follow (solo profili privati)
                </p>
              </div>
              <Switch
                checked={notifications.follows}
                onCheckedChange={(checked) => 
                  setNotifications({ ...notifications, follows: checked })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-white">Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
            
            <Separator className="bg-white/10" />
            
            <DeleteAccountButton />
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;